"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OLFeature = void 0;
const centroid_1 = __importDefault(require("@turf/centroid"));
const helpers_1 = require("@turf/helpers");
const react_ui_1 = require("@yanzi/react-ui");
const lodash_1 = require("lodash");
const LineString_1 = __importDefault(require("ol/geom/LineString"));
const Point_1 = __importDefault(require("ol/geom/Point"));
const Polygon_1 = __importDefault(require("ol/geom/Polygon"));
const proj_1 = require("ol/proj");
const Circle_1 = __importDefault(require("ol/style/Circle"));
const Fill_1 = __importDefault(require("ol/style/Fill"));
const RegularShape_1 = __importDefault(require("ol/style/RegularShape"));
const Stroke_1 = __importDefault(require("ol/style/Stroke"));
const Style_1 = __importDefault(require("ol/style/Style"));
const Text_1 = __importDefault(require("ol/style/Text"));
const react_1 = require("react");
const useMemoized_1 = require("../hooks/useMemoized");
const useOLMap_1 = require("../openlayers/useOLMap");
const useOLDraggable_1 = require("./useOLDraggable");
const useOLLayerSource_1 = require("./useOLLayerSource");
const useOLModify_1 = require("./useOLModify");
const useOLSelectionCollection_1 = require("./useOLSelectionCollection");
const useOLSnappable_1 = require("./useOLSnappable");
const OLFeature = function OLFeature(_a) {
    var { feature, onSelect, onDeselect, isSelected, onMouseOver, onMouseOut, isDraggable, onDragStart, onDragging, onDragEnd, isEditable, onClick, onEdit, isSnappable, renderMode } = _a, styleProps = __rest(_a, ["feature", "onSelect", "onDeselect", "isSelected", "onMouseOver", "onMouseOut", "isDraggable", "onDragStart", "onDragging", "onDragEnd", "isEditable", "onClick", "onEdit", "isSnappable", "renderMode"]);
    const map = useOLMap_1.useOLMap();
    const layerSpecs = react_1.useMemo(() => {
        var _a, _b;
        return ({
            zIndex: Math.floor((_a = styleProps.zIndex) !== null && _a !== void 0 ? _a : 0),
            opacity: (_b = styleProps.opacity) !== null && _b !== void 0 ? _b : 1,
            renderMode: renderMode !== null && renderMode !== void 0 ? renderMode : (styleProps.text || styleProps.shape === 'circle' || styleProps.shape === 'polygon'
                ? 'vector'
                : 'image'),
            declutter: styleProps.declutter,
        });
    }, [
        styleProps.declutter,
        styleProps.zIndex,
        styleProps.opacity,
        renderMode,
        styleProps.text,
        styleProps.shape,
    ]);
    const memoizedStyleProps = useMemoized_1.useMemoized(styleProps);
    const { addFeature, removeFeature } = useOLLayerSource_1.useOLLayerSource();
    react_1.useEffect(() => {
        addFeature(layerSpecs, feature);
        return () => {
            removeFeature(layerSpecs, feature);
        };
    }, [addFeature, feature, layerSpecs, removeFeature]);
    useOLDraggable_1.useOLDraggable(isDraggable, feature);
    useOLSnappable_1.useOLSnappable(isSnappable, feature);
    useOLModify_1.useOLModify(isEditable, feature);
    useDragActions(feature, { onDragStart, onDragging, onDragEnd });
    const { isModifying } = useModifyActions(feature, !!isEditable, { onEdit });
    react_1.useEffect(() => {
        if (!onClick) {
            return () => { };
        }
        const onMapClick = (e) => {
            map.forEachFeatureAtPixel(e.pixel, f => {
                if (f === feature) {
                    onClick();
                }
            });
        };
        map.on('click', onMapClick);
        return () => {
            map.un('click', onMapClick);
        };
    }, [feature, map, onClick]);
    const selectionCollection = useOLSelectionCollection_1.useOLSelectionCollection();
    const isMouseOver = useMouseEvents(feature, { onMouseOver, onMouseOut }, { forceMouseOver: isModifying, neededInternally: !!memoizedStyleProps._hover });
    react_1.useEffect(() => {
        if (!onSelect)
            return () => { };
        const onOLSelect = () => {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect();
        };
        feature.on('selected', onOLSelect);
        return () => {
            feature.un('selected', onOLSelect);
        };
    }, [feature, onSelect]);
    react_1.useEffect(() => {
        if (!onDeselect)
            return () => { };
        const onOLDeselect = () => {
            onDeselect === null || onDeselect === void 0 ? void 0 : onDeselect();
        };
        feature.on('deselected', onOLDeselect);
        return () => {
            feature.un('deselected', onOLDeselect);
        };
    }, [feature, onDeselect]);
    react_1.useEffect(() => {
        if (isSelected) {
            selectionCollection.push(feature);
            return () => {
                selectionCollection.remove(feature);
            };
        }
        else {
            return () => { };
        }
    }, [selectionCollection, feature, isSelected]);
    const theme = react_ui_1.useTheme();
    const getColor = react_1.useCallback(function (clr) {
        if (!clr) {
            return clr;
        }
        const parts = clr.split('.');
        if (parts[0] === 'floorplans') {
            if (parts.length < 2) {
                return lodash_1.get(theme.colors, clr, clr);
            }
            const lastPart = parts[parts.length - 1];
            for (let i = parts.length - 1; i >= 0; i--) {
                const currentParts = parts.slice(0, i);
                const key = `${currentParts.join('.')}.${lastPart}`;
                const color = lodash_1.get(theme.colors, key, undefined);
                if (color) {
                    return color;
                }
            }
            return clr;
        }
        return lodash_1.get(theme.colors, clr, clr);
    }, [theme]);
    const getStyleValue = react_1.useCallback(function (key) {
        var _a, _b;
        return (_b = (isMouseOver ? (_a = memoizedStyleProps._hover) === null || _a === void 0 ? void 0 : _a[key] : null)) !== null && _b !== void 0 ? _b : memoizedStyleProps[key];
    }, [memoizedStyleProps, isMouseOver]);
    const style = react_1.useMemo(() => {
        var _a;
        const radius = getStyleValue('radius');
        const points = getStyleValue('points');
        const backgroundColor = getStyleValue('backgroundColor');
        const textBackgroundColor = getStyleValue('textBackgroundColor');
        const scale = getStyleValue('scale');
        const zIndex = getStyleValue('zIndex');
        const marginTop = getStyleValue('marginTop');
        const marginLeft = getStyleValue('marginLeft');
        const displacement = [marginTop !== null && marginTop !== void 0 ? marginTop : 0, marginLeft !== null && marginLeft !== void 0 ? marginLeft : 0];
        const text = getStyleValue('text');
        const fontSize = getStyleValue('fontSize');
        return new Style_1.default({
            zIndex,
            stroke: getStyleValue('borderWidth')
                ? new Stroke_1.default({
                    width: applyScale(getStyleValue('borderWidth'), scale),
                    color: getColor(getStyleValue('borderColor')),
                    lineDash: getStyleValue('borderStyle') === 'dashed' ? [20, 10] : undefined,
                })
                : undefined,
            fill: getStyleValue('backgroundColor')
                ? new Fill_1.default({
                    color: getColor(getStyleValue('backgroundColor')),
                })
                : undefined,
            image: getStyleValue('shape') === 'circle' && radius
                ? new Circle_1.default({
                    radius: applyScale(radius, scale),
                    fill: backgroundColor ? new Fill_1.default({ color: getColor(backgroundColor) }) : undefined,
                    stroke: getStyleValue('borderWidth')
                        ? new Stroke_1.default({
                            width: applyScale(getStyleValue('borderWidth'), scale),
                            color: getColor(getStyleValue('borderColor')),
                        })
                        : undefined,
                    displacement,
                })
                : getStyleValue('shape') === 'polygon' && points
                    ? new RegularShape_1.default({
                        points,
                        rotation: getStyleValue('rotation'),
                        fill: backgroundColor ? new Fill_1.default({ color: getColor(backgroundColor) }) : undefined,
                        stroke: getStyleValue('borderWidth')
                            ? new Stroke_1.default({
                                width: applyScale(getStyleValue('borderWidth'), scale),
                                color: getColor(getStyleValue('borderColor')),
                            })
                            : undefined,
                        radius: applyScale(radius, scale),
                    })
                    : undefined,
            text: text
                ? new Text_1.default({
                    font: `${fontSize !== null && fontSize !== void 0 ? fontSize : `${applyScale(12, scale).toFixed(3)}px`} "Roboto", sans-serif`,
                    rotateWithView: false,
                    text: text,
                    fill: new Fill_1.default({ color: getStyleValue('color') }),
                    backgroundFill: textBackgroundColor
                        ? new Fill_1.default({ color: getColor(textBackgroundColor) })
                        : undefined,
                    backgroundStroke: textBackgroundColor
                        ? new Stroke_1.default({ color: getColor(textBackgroundColor), width: 2 })
                        : undefined,
                    padding: [2, 4, 2, 4],
                    overflow: true,
                    offsetY: (_a = getStyleValue('textOffsetY')) !== null && _a !== void 0 ? _a : undefined,
                })
                : undefined,
        });
    }, [getColor, getStyleValue]);
    react_1.useLayoutEffect(() => {
        feature.setStyle(style);
    }, [feature, style]);
    react_1.useLayoutEffect(() => {
        var _a;
        if (isMouseOver) {
            map.getTargetElement().style.cursor = (_a = getStyleValue('cursor')) !== null && _a !== void 0 ? _a : '';
            return () => {
                map.getTargetElement().style.cursor = '';
            };
        }
        return () => { };
    }, [isMouseOver, map, getStyleValue]);
    return null;
};
exports.OLFeature = OLFeature;
function useDragActions(feature, { onDragStart, onDragging, onDragEnd }) {
    react_1.useEffect(() => {
        if (!onDragStart) {
            return () => { };
        }
        const dragStart = () => {
            onDragStart({ coordinates: proj_1.toLonLat(getCenter(feature.getGeometry())) });
        };
        feature.on('dragstart', dragStart);
        return () => {
            feature.un('dragstart', dragStart);
        };
    }, [feature, onDragStart]);
    react_1.useEffect(() => {
        if (!onDragging) {
            return () => { };
        }
        const dragging = () => {
            onDragging({ coordinates: proj_1.toLonLat(getCenter(feature.getGeometry())) });
        };
        feature.on('dragging', dragging);
        return () => {
            feature.un('dragging', dragging);
        };
    }, [feature, onDragging]);
    react_1.useEffect(() => {
        if (!onDragEnd) {
            return () => { };
        }
        const dragEnd = () => {
            onDragEnd({ coordinates: proj_1.toLonLat(getCenter(feature.getGeometry())) });
        };
        feature.on('dragend', dragEnd);
        return () => {
            feature.un('dragend', dragEnd);
        };
    }, [feature, onDragEnd]);
}
function useModifyActions(feature, isEditable, { onEdit }) {
    const [isModifying, setIsModifying] = react_1.useState(false);
    react_1.useEffect(() => {
        if (!isEditable) {
            return () => { };
        }
        const editEnd = () => {
            onEdit === null || onEdit === void 0 ? void 0 : onEdit({ geometry: feature.getGeometry() });
            setIsModifying(false);
        };
        feature.on('editend', editEnd);
        return () => {
            feature.un('editend', editEnd);
        };
    }, [feature, isEditable, onEdit]);
    react_1.useEffect(() => {
        if (!isEditable) {
            return () => { };
        }
        const editStart = () => setIsModifying(true);
        feature.on('editstart', editStart);
        return () => {
            feature.un('editstart', editStart);
        };
    }, [feature, isEditable]);
    return react_1.useMemo(() => ({ isModifying }), [isModifying]);
}
function useMouseEvents(feature, { onMouseOut, onMouseOver, }, { forceMouseOver, neededInternally }) {
    const [isMouseOver_, setIsMouseOver] = react_1.useState(false);
    const isMouseOver = isMouseOver_ || forceMouseOver;
    react_1.useEffect(() => {
        if (!onMouseOver && !neededInternally) {
            return () => { };
        }
        const mouseOver = () => {
            onMouseOver === null || onMouseOver === void 0 ? void 0 : onMouseOver();
            setIsMouseOver(true);
        };
        feature.on('mouseover', mouseOver);
        return () => {
            feature.un('mouseover', mouseOver);
        };
    }, [onMouseOver, setIsMouseOver, feature, neededInternally]);
    react_1.useEffect(() => {
        if (!onMouseOut && !neededInternally) {
            return () => { };
        }
        const mouseOut = () => {
            onMouseOut === null || onMouseOut === void 0 ? void 0 : onMouseOut();
            setIsMouseOver(false);
        };
        feature.on('mouseout', mouseOut);
        return () => {
            feature.un('mouseout', mouseOut);
        };
    }, [onMouseOut, setIsMouseOver, feature, neededInternally]);
    return isMouseOver;
}
function applyScale(value, scale) {
    if (value === undefined) {
        return value;
    }
    else {
        if (scale === undefined) {
            return value;
        }
        return (value * scale);
    }
}
function getCenter(geometry) {
    var _a;
    try {
        if (geometry instanceof Point_1.default) {
            return geometry.getCoordinates();
        }
        else if (geometry instanceof Polygon_1.default) {
            const center = (_a = centroid_1.default(helpers_1.polygon(geometry.getCoordinates()))) === null || _a === void 0 ? void 0 : _a.geometry.coordinates;
            if (center) {
                return center;
            }
        }
        else if (geometry instanceof LineString_1.default) {
            return geometry.getFlatMidpoint();
        }
    }
    catch (_b) { }
    throw new Error('Does not know how to calculate center of geometry type: ' + geometry.getType());
}
//# sourceMappingURL=OLFeature.js.map